import NoData from '@/components/common/NoData'
import { useGuidedEdit } from '@/features/config/contexts/guided-edit-context'
import StatusInterface from '@/components/common/StatusInterface'
import ButtonField from '@/components/form-control/ButtonField'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import { flyInVariantsDashboard } from '@/constant/common'
import useSearchInterfaceId from '@/features/admin/hooks/interface/useSearchInterfaceId'
import { useAuthContext } from '@/features/auth/context'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import { AreaType } from '@/types/areaZone'
import { Stack } from '@mui/material'
import { motion } from 'framer-motion'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { ConfigLayout } from '../../components/common'
import ConfigHeader from '../../components/common/Header'
import { useConfigUI } from '../../context'
import useSearchArea from '../../hooks/areasandzones/useSearchArea'
import AreasAndZonesTableGrid, {
  type AreasGridActions,
} from './AreasAndZonesTableGrid'
import classes from './style.module.scss'

// Area writes must be serialised: the API returns 500 under concurrent writes.


// `titleSlot` switches the component into embedded (column) mode: it renders
// inline — with the title on the toolbar — instead of as a full page, so it can
// sit as a fourth column beside Plant / Soil / Emitter.
// `interfaceId` lets an embedder (e.g. the guided setup) scope every fetch and
// save to an explicit interface; when omitted the globally-selected interface
// from AuthContext is used, so the normal screen behaves identically.
const AreasAndZones = ({
  titleSlot,
  interfaceId: propInterfaceId,
}: {
  titleSlot?: ReactNode
  interfaceId?: number | string
}) => {
  const MotionDiv = motion.div
  const { openSidebar, setSidebarView } = useConfigUI()
  const { openModal, setModalView } = useConfigUI()
  const { interfaceId: authInterfaceId, statusInterfaceId } = useAuthContext()
  // `statusInterfaceId` stays sourced from AuthContext — it drives the offline
  // overlay, not the data scope.
  const interfaceId = propInterfaceId ?? authInterfaceId
  const { data: statusInterface } = useSearchInterfaceId(interfaceId + '')
  const { isViewOnly } = usePermission()
  const { t } = useTranslation()
  // SCRUM-1032: inside the guided setup the grid opens in bulk edit and its
  // pending rows are flushed when the wizard moves on.
  const bulkEditByDefault = useGuidedEdit('areas', () =>
    gridActions.current?.save(),
  )
  const [bulkEditMode, setBulkEditMode] = useState(bulkEditByDefault)

  // The whole tree is fetched in one request: sub-areas have to arrive with
  // their parent to nest correctly, so this list is never paged.
  const [allRows, setAllRows] = useState<AreaType[]>([])
  const gridActions = useRef<AreasGridActions | null>(null)

  const { data, refetch, isLoading } = useSearchArea(
    { PageSize: 0, PageIndex: 1 },
    Number(interfaceId || 0),
  )

  useEffect(() => {
    if (!data) return
    setAllRows(data.list)
  }, [data])

  const handleRefetch = useCallback(async () => {
    await refetch()
  }, [refetch])

  const handleToggleBulkEdit = async () => {
    if (bulkEditMode) {
      const result = await gridActions.current?.save()
      // Stay in bulk edit when something failed, so the pending changes are
      // not lost and can be retried.
      if (result && result.failed > 0) return
    }
    setBulkEditMode(!bulkEditMode)
  }

  const handleCancelBulkEdit = () => {
    gridActions.current?.cancel()
    setBulkEditMode(false)
  }

  const handleDeleteItem = (
    id: number,
    interfaceId: string,
    mode: string,
    name: string,
  ) => {
    if (isViewOnly) return

    // Deletion is gated by the backend, which only blocks a sub-area that
    // still has a station attached (Area_Used_Not_Delete). Do not add a
    // client-side "last sub-area" rule — it has no backend counterpart and
    // wrongly blocks deleting an unattached sub-area.
    openModal()
    setModalView({
      type: 'DELETE_AREA_VIEW',
      id: id.toString(),
      interfaceId: interfaceId,
      mode: mode,
      name: name,
      onSuccess: async () => {
        setAllRows((prev) => {
          const isSubArea = prev.some((area) =>
            area.subAreas?.some((sub) => sub.id === id),
          )
          if (isSubArea) {
            return prev.map((area) => ({
              ...area,
              subAreas: area.subAreas.filter((sub) => sub.id !== id),
            }))
          }
          return prev.filter((area) => area.id !== id)
        })
      },
    })
  }

  const handleAddSubItem = (id: number, parentName: string) => {
    if (isViewOnly) return
    openSidebar()
    setSidebarView({ type: 'ADD_SUB_AREA_VIEW', id: id.toString(), parentName })
  }

  const handleEditSubArea = (
    id: number,
    parentId: string,
    name: string,
    options?: { isParent?: boolean },
  ) => {
    if (isViewOnly) return
    const { isParent } = options || {}
    openSidebar()
    setSidebarView({
      type: isParent ? 'ADD_AREA_VIEW' : 'EDIT_SUB_AREA_VIEW',
      id: id.toString(),
      parentId,
      name,
    })
  }

  const body = (
    <>
      <Stack
        className={`${classes.wrapper} ${
          statusInterface &&
          statusInterface.state === 'Offline' &&
          statusInterfaceId &&
          'wrapper-disabled'
        }`}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={titleSlot ? 'space-between' : 'flex-end'}
          mb="12px"
        >
          {titleSlot}
          <Stack direction="row" spacing={1.5}>
            {bulkEditMode ? (
              <>
                {/* SCRUM-1032: the guided wizard's Next flushes every grid,
                    so its Cancel / Save are hidden. */}
                {!bulkEditByDefault && (
                  <ButtonField
                    startIcon={<CancelIcon />}
                    style={{
                      height: '32px',
                      minWidth: '70px',
                      borderRadius: '5px',
                      backgroundColor: '#fff2f2',
                      color: '#dc3545',
                      border: '1px solid #f3c2c5',
                    }}
                    name={t('commonActionsCancel')}
                    onClick={handleCancelBulkEdit}
                  />
                )}
                {!bulkEditByDefault && (
                  <ButtonField
                    startIcon={<SaveIcon />}
                    style={{
                      height: '32px',
                      minWidth: '70px',
                      borderRadius: '5px',
                      backgroundColor: '#004744',
                      color: '#fff',
                    }}
                    name={t('commonActionsSave')}
                    onClick={handleToggleBulkEdit}
                  />
                )}
              </>
            ) : (
              !bulkEditByDefault && (
                <ButtonField
                  startIcon={<EditIcon />}
                  style={{
                    height: '32px',
                    minWidth: '89px',
                    borderRadius: '5px',
                    opacity: isViewOnly ? 0.6 : 1,
                  }}
                  name={t('commonActionsBulkEdit')}
                  onClick={handleToggleBulkEdit}
                  disabled={isViewOnly}
                />
              )
            )}
          </Stack>
        </Stack>

        <MotionDiv
          initial="initial"
          animate="animate"
          variants={flyInVariantsDashboard}
          className={classes.content}
        >
          {/* Mounted while empty in two cases: bulk edit, because the "Add
              Area" create row is pinned to the grid and unmounting would
              remove the only way to add the first area; and the first load,
              so the grid can draw its own skeleton rows instead of a spinner
              over a blank panel. */}
          {(allRows.length > 0 || bulkEditMode || isLoading) && (
            <AreasAndZonesTableGrid
              rows={allRows}
              interfaceId={Number(interfaceId)}
              loading={isLoading && allRows.length === 0}
              onEditItem={handleEditSubArea}
              onDeleteItem={handleDeleteItem}
              onAddSubItem={handleAddSubItem}
              bulkEditMode={bulkEditMode}
              onRefetch={handleRefetch}
              actionsRef={gridActions}
            />
          )}

          {!isLoading && allRows.length === 0 && data && !bulkEditMode && (
            <NoData text={t('commonStatusNoData')} />
          )}
        </MotionDiv>
      </Stack>
    </>
  )

  // Embedded (4th column): render inline, no page chrome.
  if (titleSlot) return body

  return (
    <ConfigLayout
      title={t('navigationConfigAreasAndSubAreas')}
      header={
        <ConfigHeader
          subtitle={t('dataConfig')}
          title={t('navigationConfigAreasAndSubAreas')}
        />
      }
    >
      {body}
      <StatusInterface />
    </ConfigLayout>
  )
}

export default AreasAndZones
